import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import FavoritesOnlyChip from 'components/FavoritesOnlyChip/FavoritesOnlyChip'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import Title from 'components/Title/Title'
import { useIsMobile } from 'hooks/useIsMobile'
import AddRobotModal from 'pages/Assets/robots/AddRobotModal/AddRobotModal'
import AllRobotsOnMap from 'pages/Assets/robots/AllRobotsOnMap/AllRobotsOnMap'
import AddStationModal from 'pages/Assets/stations/AddStationModal/AddStationModal'
import AllStationsOnMap from 'pages/Assets/stations/AllStationsOnMap/AllStationsOnMap'
import AssetsMaintenancePanel from 'pages/Assets/maintenance/AssetsMaintenancePanel'
import MaintenancePointsOnMap from 'components/MaintenancePointsOnMap/MaintenancePointsOnMap'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { GAUSIUM_ENABLED, ASBIS_ENABLED, PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, ILot, IMaintenancePoint, IRobotInfo, IStation } from 'utils/interfaces'
import { VENDOR, getVendorDisplayName } from 'utils/vendorDisplayName'

import AssetsRobotsPanel from './AssetsRobotsPanel'
import AssetsStationsPanel from './AssetsStationsPanel'
import { AssetsTab } from './assetsTab'

import style from './AssetsView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'

type AssetsVendorFilter = typeof VENDOR.AUTONOMICS | typeof VENDOR.GAUSIUM | typeof VENDOR.ASBIS | 'ALL'

/** New Station create flow. */
const NEW_STATION_ENABLED = true

export type AssetsViewProps = {
  variant: 'page' | 'panel'
  activeTab: AssetsTab
  onActiveTabChange: (tab: AssetsTab) => void
  facilityId?: number
  robots: IRobotInfo[]
  robotsForMap: IRobotInfo[]
  stations: IStation[]
  stationsForMap: IStation[]
  lots: ILot[]
  maintenancePoints?: IMaintenancePoint[]
  onRobotsUpdated: () => void
  onStationsChanged: () => void
  onStationsUpdated: () => void
  onLiveStationFields?: (fieldsById: Record<number, Partial<IStation>>) => void
  onMaintenancePointsChanged?: () => void
  loadingProgress?: PROGRESS_TYPES
  searchQuery?: string
  setSearchQuery?: (query: string) => void
  stationSearchQuery?: string
  setStationSearchQuery?: (query: string) => void
  robotsFavoritesOnly?: boolean
  setRobotsFavoritesOnly?: (value: boolean) => void
  stationsFavoritesOnly?: boolean
  setStationsFavoritesOnly?: (value: boolean) => void
  vendorFilter?: AssetsVendorFilter
  setVendorFilter?: (vendor: AssetsVendorFilter) => void
  robotsCurrentPage?: number
  setRobotsCurrentPage?: React.Dispatch<React.SetStateAction<number>>
  robotsTotalCount?: number
  robotsPageSize?: number
  onRobotsPageSizeChange?: (size: number) => void
  robotsPageInput?: string
  setRobotsPageInput?: (val: string) => void
  handleRobotsPageJump?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  stationsCurrentPage?: number
  setStationsCurrentPage?: React.Dispatch<React.SetStateAction<number>>
  stationsTotalCount?: number
  stationsPageSize?: number
  onStationsPageSizeChange?: (size: number) => void
  stationsPageInput?: string
  setStationsPageInput?: (val: string) => void
  handleStationsPageJump?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  hideDeleteRobots?: boolean
  onExcludeRobot?: (robot: IRobotInfo) => Promise<void> | void
  highlightedRobotId?: number
  onHighlightedRobotIdChange?: (id?: number) => void
  highlightedStationId?: number
  onHighlightedStationIdChange?: (id?: number) => void
  highlightedMaintenancePointId?: number
  onHighlightedMaintenancePointIdChange?: (id?: number) => void
  panelRobotsPage?: number
  panelRobotsPageSize?: number
  panelRobotsPageInput?: string
  setPanelRobotsPage?: React.Dispatch<React.SetStateAction<number>>
  setPanelRobotsPageInput?: (val: string) => void
  onPanelRobotsPageJump?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  panelStationsPage?: number
  panelStationsPageSize?: number
  panelStationsPageInput?: string
  setPanelStationsPage?: React.Dispatch<React.SetStateAction<number>>
  setPanelStationsPageInput?: (val: string) => void
  onPanelStationsPageJump?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  panelMaintenancePage?: number
  panelMaintenancePageSize?: number
  panelMaintenancePageInput?: string
  setPanelMaintenancePage?: React.Dispatch<React.SetStateAction<number>>
  setPanelMaintenancePageInput?: (val: string) => void
  onPanelMaintenancePageJump?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const AssetsView = ({
  variant,
  activeTab,
  onActiveTabChange,
  facilityId,
  robots,
  robotsForMap,
  stations,
  stationsForMap,
  lots,
  maintenancePoints = [],
  onRobotsUpdated,
  onStationsChanged,
  onStationsUpdated,
  onLiveStationFields,
  onMaintenancePointsChanged,
  loadingProgress = PROGRESS_TYPES.SUCCESS,
  searchQuery = '',
  setSearchQuery,
  stationSearchQuery = '',
  setStationSearchQuery,
  robotsFavoritesOnly = false,
  setRobotsFavoritesOnly,
  stationsFavoritesOnly = false,
  setStationsFavoritesOnly,
  vendorFilter = 'ALL',
  setVendorFilter,
  robotsCurrentPage = 1,
  setRobotsCurrentPage,
  robotsTotalCount = 0,
  robotsPageSize = 20,
  onRobotsPageSizeChange,
  robotsPageInput = '1',
  setRobotsPageInput,
  handleRobotsPageJump,
  stationsCurrentPage = 1,
  setStationsCurrentPage,
  stationsTotalCount = 0,
  stationsPageSize = 20,
  onStationsPageSizeChange,
  stationsPageInput = '1',
  setStationsPageInput,
  handleStationsPageJump,
  hideDeleteRobots,
  onExcludeRobot,
  highlightedRobotId: highlightedRobotIdProp,
  onHighlightedRobotIdChange,
  highlightedStationId: highlightedStationIdProp,
  onHighlightedStationIdChange,
  highlightedMaintenancePointId: highlightedMaintenancePointIdProp,
  onHighlightedMaintenancePointIdChange,
  panelRobotsPage,
  panelRobotsPageSize,
  panelRobotsPageInput,
  setPanelRobotsPage,
  setPanelRobotsPageInput,
  onPanelRobotsPageJump,
  panelStationsPage,
  panelStationsPageSize,
  panelStationsPageInput,
  setPanelStationsPage,
  setPanelStationsPageInput,
  onPanelStationsPageJump,
  panelMaintenancePage,
  panelMaintenancePageSize,
  panelMaintenancePageInput,
  setPanelMaintenancePage,
  setPanelMaintenancePageInput,
  onPanelMaintenancePageJump,
}: AssetsViewProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [internalHighlightedRobotId, setInternalHighlightedRobotId] = useState<number | undefined>()
  const [internalHighlightedStationId, setInternalHighlightedStationId] = useState<number | undefined>()
  const [internalHighlightedMaintenancePointId, setInternalHighlightedMaintenancePointId] = useState<number | undefined>()
  const [addingNewRobot, setAddingNewRobot] = useState(false)
  const [addingNewStation, setAddingNewStation] = useState(false)
  const [showTable, setShowTable] = useState(true)

  const highlightedRobotId = highlightedRobotIdProp ?? internalHighlightedRobotId
  const highlightedStationId = highlightedStationIdProp ?? internalHighlightedStationId
  const highlightedMaintenancePointId = highlightedMaintenancePointIdProp ?? internalHighlightedMaintenancePointId
  const showRobotsTab = activeTab === 'robots'
  const showStationsTab = activeTab === 'stations'
  const showMaintenanceTab = activeTab === 'maintenance' && facilityId != null
  const showMaintenanceTabUi = facilityId != null

  useEffect(() => {
    if (!showMaintenanceTabUi && activeTab === 'maintenance') {
      onActiveTabChange('robots')
    }
  }, [activeTab, onActiveTabChange, showMaintenanceTabUi])

  const setHighlightedRobotId = (id?: number) => {
    if (onHighlightedRobotIdChange) {
      onHighlightedRobotIdChange(id)
    } else {
      setInternalHighlightedRobotId(id)
    }
  }

  const setHighlightedStationId = (id?: number) => {
    if (onHighlightedStationIdChange) {
      onHighlightedStationIdChange(id)
    } else {
      setInternalHighlightedStationId(id)
    }
  }

  const setHighlightedMaintenancePointId = (id?: number) => {
    if (onHighlightedMaintenancePointIdChange) {
      onHighlightedMaintenancePointIdChange(id)
    } else {
      setInternalHighlightedMaintenancePointId(id)
    }
  }

  const tabIndexForActiveTab = showRobotsTab ? 0 : showStationsTab ? 1 : 2

  const onMaintenancePointClickedFromMap = (id?: number) => {
    if (id == null) {
      setHighlightedMaintenancePointId(undefined)
      return
    }
    if (setPanelMaintenancePage && setPanelMaintenancePageInput && panelMaintenancePageSize) {
      const idx = maintenancePoints.findIndex(point => point.id === id)
      if (idx >= 0) {
        const page = Math.floor(idx / panelMaintenancePageSize) + 1
        setPanelMaintenancePage(page)
        setPanelMaintenancePageInput(String(page))
      }
    }
    setHighlightedMaintenancePointId(id)
  }

  const onMapInited = () => {
    const nextMap = mapRef.current?.getMap()
    if (nextMap) {
      setMap(nextMap)
    }
  }

  const onRobotClickedFromMap = (id?: number) => {
    if (id == null) {
      setHighlightedRobotId(undefined)
      return
    }
    if (setRobotsCurrentPage && setRobotsPageInput) {
      const idx = robotsForMap.findIndex(r => r.id === id)
      if (idx >= 0) {
        const page = Math.floor(idx / robotsPageSize) + 1
        setRobotsCurrentPage(page)
        setRobotsPageInput(String(page))
      }
    }
    setHighlightedRobotId(id)
  }

  const onStationClickedFromMap = (id?: number) => {
    if (id == null) {
      setHighlightedStationId(undefined)
      return
    }
    if (setStationsCurrentPage && setStationsPageInput) {
      const idx = stationsForMap.findIndex(s => s.id === id)
      if (idx >= 0) {
        const page = Math.floor(idx / stationsPageSize) + 1
        setStationsCurrentPage(page)
        setStationsPageInput(String(page))
      }
    }
    setHighlightedStationId(id)
  }

  const vendorDropdownOptions: IOption[] = [
    { value: 'ALL', text: 'All vendors' },
    { value: VENDOR.AUTONOMICS, text: 'Autonomics' },
    ...(GAUSIUM_ENABLED ? [{ value: VENDOR.GAUSIUM, text: 'Gausium' }] : []),
    ...(ASBIS_ENABLED ? [{ value: VENDOR.ASBIS, text: 'Asbis' }] : []),
  ]

  const tabPanels = (
    <>
      <div className={clsx(style.tabPanel, { [style.tabPanelHidden]: !showRobotsTab })}>
        <AssetsRobotsPanel
          variant={variant}
          robots={robots}
          robotsForStats={robotsForMap}
          stations={stationsForMap}
          loadingProgress={variant === 'page' ? loadingProgress : undefined}
          highlightedRobotId={highlightedRobotId}
          onRobotsUpdated={onRobotsUpdated}
          onStationsChanged={onStationsChanged}
          hideDeleteRobots={hideDeleteRobots}
          onExcludeRobot={onExcludeRobot}
          currentPage={robotsCurrentPage}
          setCurrentPage={setRobotsCurrentPage}
          totalCount={robotsTotalCount}
          pageSize={robotsPageSize}
          onPageSizeChange={onRobotsPageSizeChange}
          pageInput={robotsPageInput}
          setPageInput={setRobotsPageInput}
          handlePageJump={handleRobotsPageJump}
          panelPage={panelRobotsPage}
          panelPageSize={panelRobotsPageSize}
          panelPageInput={panelRobotsPageInput}
          setPanelPage={setPanelRobotsPage}
          setPanelPageInput={setPanelRobotsPageInput}
          onPanelPageJump={onPanelRobotsPageJump}
          isActive={showRobotsTab}
          emptyTitle={robotsFavoritesOnly ? t('favorites.empty_robots') : undefined}
          emptyDescription={robotsFavoritesOnly ? t('favorites.empty_robots_description') : undefined}
        />
      </div>
      <div className={clsx(style.tabPanel, { [style.tabPanelHidden]: !showStationsTab })}>
        <AssetsStationsPanel
          variant={variant}
          stations={stations}
          stationsForStats={stationsForMap}
          lots={lots}
          loadingProgress={variant === 'page' ? loadingProgress : undefined}
          highlightedStationId={highlightedStationId}
          onStationsUpdated={onStationsUpdated}
          onStationsChanged={onStationsChanged}
          onLiveStationFields={onLiveStationFields}
          currentPage={stationsCurrentPage}
          setCurrentPage={setStationsCurrentPage}
          totalCount={stationsTotalCount}
          pageSize={stationsPageSize}
          onPageSizeChange={onStationsPageSizeChange}
          pageInput={stationsPageInput}
          setPageInput={setStationsPageInput}
          handlePageJump={handleStationsPageJump}
          panelPage={panelStationsPage}
          panelPageSize={panelStationsPageSize}
          panelPageInput={panelStationsPageInput}
          setPanelPage={setPanelStationsPage}
          setPanelPageInput={setPanelStationsPageInput}
          onPanelPageJump={onPanelStationsPageJump}
          isActive={showStationsTab}
          emptyTitle={stationsFavoritesOnly ? t('favorites.empty_stations') : undefined}
          emptyDescription={stationsFavoritesOnly ? t('favorites.empty_stations_description') : undefined}
        />
      </div>
      {showMaintenanceTabUi && (
        <div className={clsx(style.tabPanel, { [style.tabPanelHidden]: !showMaintenanceTab })}>
          <AssetsMaintenancePanel
            facilityId={facilityId!}
            points={maintenancePoints}
            loadingProgress={variant === 'page' ? loadingProgress : undefined}
            highlightedPointId={highlightedMaintenancePointId}
            onPointsUpdated={onMaintenancePointsChanged ?? (() => {})}
            onPointClicked={setHighlightedMaintenancePointId}
            panelPage={panelMaintenancePage}
            panelPageSize={panelMaintenancePageSize}
            panelPageInput={panelMaintenancePageInput}
            setPanelPage={setPanelMaintenancePage}
            setPanelPageInput={setPanelMaintenancePageInput}
            onPanelPageJump={onPanelMaintenancePageJump}
            isActive={showMaintenanceTab}
            hideDelete={false}
          />
        </div>
      )}
    </>
  )

  const tabContent = (
    <>
      <AdaptiveTabs
        value={tabIndexForActiveTab}
        onChange={(_, value) => {
          const nextTab: AssetsTab = value === 0 ? 'robots' : value === 1 ? 'stations' : 'maintenance'
          onActiveTabChange(nextTab)
          setHighlightedRobotId(undefined)
          setHighlightedStationId(undefined)
          setHighlightedMaintenancePointId(undefined)
        }}
        indicatorColor="primary"
        sx={{ height: 45, minHeight: 45, flexShrink: 0 }}
      >
        <StyledTab
          label={t('assets.tab_robots')}
          sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
          disableRipple
        />
        <StyledTab
          label={t('assets.tab_stations')}
          sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
          disableRipple
        />
        {showMaintenanceTabUi && (
          <StyledTab
            label="Maintenance"
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
            disableRipple
          />
        )}
      </AdaptiveTabs>
      <div className={style.tabContent}>{tabPanels}</div>
    </>
  )

  if (variant === 'panel') {
    return (
      <div className={style.panelRoot}>
        {tabContent}
      </div>
    )
  }

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <Title text={t('assets.title')} />
            <div className={style.headerRight}>
              {showRobotsTab && (
                <Button.Standart className={style.actionButton} onClick={() => setAddingNewRobot(true)}>
                  {t('robots.add_robot')}
                </Button.Standart>
              )}
              {showStationsTab && (
                <Button.Standart
                  className={style.actionButton}
                  disabled={!NEW_STATION_ENABLED}
                  onClick={() => setAddingNewStation(true)}
                >
                  {t('stations.add_station')}
                </Button.Standart>
              )}
            </div>
          </div>
        </div>

        {showRobotsTab && setSearchQuery && setVendorFilter && (
          <div className={style.filtersAside}>
            <Dropdown
              className={style.filterDropdown}
              minWidth={isMobile ? 120 : 160}
              useSmallDropdownStyle={isMobile}
              currentOption={
                vendorFilter === 'ALL'
                  ? undefined
                  : { value: vendorFilter, text: getVendorDisplayName(vendorFilter) }
              }
              placeholder="All vendors"
              options={vendorDropdownOptions}
              onClickOption={(opt: IOption) => setVendorFilter(String(opt.value) as AssetsVendorFilter)}
            />
            <input
              className={style.searchInput}
              type="text"
              placeholder={t('assets.search_robots')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {setRobotsFavoritesOnly && (
              <FavoritesOnlyChip
                pressed={robotsFavoritesOnly}
                onClick={() => setRobotsFavoritesOnly(!robotsFavoritesOnly)}
              />
            )}
          </div>
        )}

        {showStationsTab && setStationSearchQuery && (
          <div className={style.filtersAside}>
            <input
              className={style.searchInput}
              type="text"
              placeholder={t('assets.search_stations')}
              value={stationSearchQuery}
              onChange={e => setStationSearchQuery(e.target.value)}
            />
            {setStationsFavoritesOnly && (
              <FavoritesOnlyChip
                pressed={stationsFavoritesOnly}
                onClick={() => setStationsFavoritesOnly(!stationsFavoritesOnly)}
              />
            )}
          </div>
        )}

        <Box className={clsx(style.box, { [style.boxHidden]: !showTable })}>
          {loadingProgress === PROGRESS_TYPES.WORK ? <Loader /> : tabContent}
        </Box>

        <div className={style.mapWrapper}>
          <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited}>
            <AllStationsOnMap
              map={map as any}
              stations={showStationsTab ? stationsForMap : []}
              lots={lots}
              onStationClicked={onStationClickedFromMap}
              interactive={showStationsTab}
              autoFitBounds={showStationsTab}
              offset={isMobile ? 0 : 600}
            />
            <AllRobotsOnMap
              map={map as any}
              robots={showRobotsTab ? robotsForMap : []}
              onRobotClicked={onRobotClickedFromMap}
              interactive={showRobotsTab}
              autoFitBounds={showRobotsTab}
              offset={isMobile ? 0 : 600}
            />
            {showMaintenanceTab && facilityId != null && (
              <MaintenancePointsOnMap
                map={map as any}
                points={maintenancePoints}
                highlightedPointId={highlightedMaintenancePointId}
                onPointClicked={onMaintenancePointClickedFromMap}
              />
            )}
          </MapboxCommon>
        </div>

        {isMobile && (
          <button
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowTable(prev => !prev)}
            title={showTable ? 'Switch to Map' : 'Switch to Table'}
            type="button"
            style={{ position: 'absolute', bottom: '24px', right: '24px' }}
          >
            {showTable ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </button>
        )}
      </div>

      <AddRobotModal
        isOpen={addingNewRobot}
        onClose={() => setAddingNewRobot(false)}
        onSave={() => {
          onRobotsUpdated()
          setAddingNewRobot(false)
        }}
        robots={robotsForMap}
      />
      <AddStationModal
        isOpen={addingNewStation}
        stations={stationsForMap}
        onClose={() => setAddingNewStation(false)}
        onSave={onStationsUpdated}
        onStationsChanged={onStationsChanged}
        defaultFacilityId={facilityId}
      />
    </div>
  )
}

export default AssetsView
